import { useState, useEffect, useCallback, useMemo } from 'react';
import { Grid, makeStyles, Button, Box, TextField, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import RestoreIcon from '@material-ui/icons/Restore';
import { Content, Page } from '@backstage/core-components';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  OnboardingSection,
  TeamLinksSection,
  GitRepoSection,
  GcpProjectsSection,
  PullRequestsSection,
  GithubTeamsBar,
  homepageLinksApiRef,
} from '@internal/plugin-homepage-links';
import type { GithubTeam } from '@internal/plugin-homepage-links';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
  },
  headerActions: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(3),
    zIndex: 1,
    display: 'flex',
    gap: theme.spacing(1),
  },
  restoreButton: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
  // Custom header styles - placed inside content area
  customHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(3, 2),
    marginBottom: theme.spacing(2),
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  customHeaderCompact: {
    padding: theme.spacing(2),
  },
  headerTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    margin: 0,
    color: theme.palette.text.primary,
  },
  headerTitleCompact: {
    fontSize: '1.5rem',
  },
  headerSubtitle: {
    fontSize: '0.95rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  headerEditField: {
    marginBottom: theme.spacing(1),
    '& .MuiInputBase-input': {
      color: theme.palette.text.primary,
      fontSize: '1.5rem',
      fontWeight: 700,
    },
  },
  subtitleEditField: {
    '& .MuiInputBase-input': {
      color: theme.palette.text.secondary,
      fontSize: '0.95rem',
    },
  },
}));

// Wrapper to safely use API
function useHomepageLinksApi() {
  try {
    return useApi(homepageLinksApiRef);
  } catch (e) {
    console.error('Failed to get homepage links API:', e);
    return null;
  }
}

export const HomePage = () => {
  const classes = useStyles();
  const api = useHomepageLinksApi();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [editMode, setEditMode] = useState(false);
  const [hiddenSections, setHiddenSections] = useState<string[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // GitHub teams state for filtering
  const [selectedTeamSlugs, setSelectedTeamSlugs] = useState<string[]>([]);
  // GitHub user state for filtering
  const [_githubUsername, setGithubUsername] = useState<string | null>(null);
  const [userSelected, setUserSelected] = useState(true);

  // Convert selected team slugs to Backstage group refs for filtering
  const selectedGroupRefs = useMemo(() => {
    return selectedTeamSlugs.map(slug => `group:default/${slug}`);
  }, [selectedTeamSlugs]);

  // Track whether default selections have been applied
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Handler for when teams are loaded from GitHub
  const handleTeamsLoaded = useCallback((teams: GithubTeam[]) => {
    // Only initialize with all teams if defaults haven't been applied yet
    if (!defaultsApplied) {
      setSelectedTeamSlugs(teams.map(t => t.slug));
    }
  }, [defaultsApplied]);

  // Handler for team selection changes
  const handleTeamSelectionChange = useCallback((newSelectedTeams: string[]) => {
    setSelectedTeamSlugs(newSelectedTeams);
  }, []);

  // Handler for when username is loaded from GitHub
  const handleUsernameLoaded = useCallback((username: string) => {
    setGithubUsername(username);
  }, []);

  // Handler for user selection changes
  const handleUserSelectionChange = useCallback((selected: boolean) => {
    setUserSelected(selected);
  }, []);

  // Handler for saving default selections
  const handleSaveDefault = useCallback(async () => {
    if (!api) return;
    try {
      await api.updatePreferences({
        defaultSelectedTeams: selectedTeamSlugs,
        defaultUserSelected: userSelected,
      });
      setDefaultsApplied(true);
    } catch (err) {
      console.error('Failed to save default selections:', err);
    }
  }, [api, selectedTeamSlugs, userSelected]);

  // Header state
  const [userFullName, setUserFullName] = useState<string>('');
  const [headerTitle, setHeaderTitle] = useState<string>('');
  const [headerSubtitle, setHeaderSubtitle] = useState<string>('');
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [editingSubtitle, setEditingSubtitle] = useState<string>('');

  // Fetch user's full name from catalog
  const fetchUserFullName = useCallback(async () => {
    try {
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef;

      if (userEntityRef) {
        try {
          const entity = await catalogApi.getEntityByRef(userEntityRef);
          if (entity) {
            // Try to get display name from entity
            const spec = entity.spec as Record<string, unknown> | undefined;
            const profile = spec?.profile as Record<string, unknown> | undefined;
            const displayName = (profile?.displayName as string)
              || entity.metadata?.title
              || entity.metadata?.name
              || '';
            setUserFullName(displayName);
          }
        } catch (err) {
          // Entity might not exist in catalog, extract name from ref
          const namePart = userEntityRef.split('/').pop() || '';
          // Convert kebab-case or snake_case to Title Case
          const formatted = namePart
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
          setUserFullName(formatted);
        }
      }
    } catch (err) {
      console.error('Failed to fetch user identity:', err);
    }
  }, [identityApi, catalogApi]);

  // Load preferences including header settings and default selections
  const loadPreferences = useCallback(async () => {
    if (!api) return;
    try {
      const prefs = await api.getPreferences();
      setHiddenSections(prefs.hiddenSections || []);
      if (prefs.headerTitle !== undefined) {
        setHeaderTitle(prefs.headerTitle);
      }
      if (prefs.headerSubtitle !== undefined) {
        setHeaderSubtitle(prefs.headerSubtitle);
      }
      // Apply saved default selections if they exist
      if (prefs.defaultSelectedTeams !== undefined) {
        setSelectedTeamSlugs(prefs.defaultSelectedTeams);
        setDefaultsApplied(true);
      }
      if (prefs.defaultUserSelected !== undefined) {
        setUserSelected(prefs.defaultUserSelected);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchUserFullName();
    loadPreferences();
  }, [fetchUserFullName, loadPreferences]);

  const handleRestoreHiddenSections = useCallback(async () => {
    if (!api) return;
    try {
      await api.updatePreferences({ hiddenSections: [] });
      setHiddenSections([]);
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Failed to restore hidden sections:', err);
    }
  }, [api]);

  const handleSectionHidden = useCallback((sectionKey: string) => {
    setHiddenSections(prev =>
      prev.includes(sectionKey) ? prev : [...prev, sectionKey]
    );
  }, []);

  // Handle edit mode toggle with header preference saving
  const handleEditModeToggle = useCallback(async () => {
    if (editMode) {
      // Exiting edit mode - save header preferences
      if (api) {
        try {
          await api.updatePreferences({
            headerTitle: editingTitle,
            headerSubtitle: editingSubtitle,
          });
          setHeaderTitle(editingTitle);
          setHeaderSubtitle(editingSubtitle);
        } catch (err) {
          console.error('Failed to save header preferences:', err);
        }
      }
    } else {
      // Entering edit mode - initialize editing values
      setEditingTitle(headerTitle);
      setEditingSubtitle(headerSubtitle);
    }
    setEditMode(!editMode);
  }, [editMode, api, editingTitle, editingSubtitle, headerTitle, headerSubtitle]);

  // Get the display title
  const getDisplayTitle = useCallback(() => {
    if (headerTitle) {
      return headerTitle;
    }
    return userFullName
      ? `Badal.io portal for ${userFullName}`
      : 'Badal.io portal';
  }, [headerTitle, userFullName]);

  const hasHiddenSections = hiddenSections.length > 0;
  const hasSubtitle = editMode ? editingSubtitle.length > 0 : headerSubtitle.length > 0;

  return (
    <Page themeId="home">
      <Content>
        <Box
          className={`${classes.customHeader} ${!hasSubtitle ? classes.customHeaderCompact : ''}`}
        >
          {editMode ? (
            <>
              <TextField
                value={editingTitle}
                onChange={e => setEditingTitle(e.target.value)}
                placeholder={`Badal.io portal for ${userFullName || 'you'}`}
                fullWidth
                className={classes.headerEditField}
              />
              <TextField
                value={editingSubtitle}
                onChange={e => setEditingSubtitle(e.target.value)}
                placeholder="Add a subtitle (optional)"
                fullWidth
                className={classes.subtitleEditField}
              />
            </>
          ) : (
            <>
              <Typography
                variant="h1"
                className={`${classes.headerTitle} ${!hasSubtitle ? classes.headerTitleCompact : ''}`}
              >
                {getDisplayTitle()}
              </Typography>
              {headerSubtitle && (
                <Typography className={classes.headerSubtitle}>
                  {headerSubtitle}
                </Typography>
              )}
            </>
          )}
          <Box className={classes.headerActions}>
            {editMode && hasHiddenSections && (
              <Button
                size="small"
                variant="contained"
                startIcon={<RestoreIcon />}
                onClick={handleRestoreHiddenSections}
                className={classes.restoreButton}
              >
                Restore hidden sections
              </Button>
            )}
            <Button
              size="small"
              variant={editMode ? 'contained' : 'outlined'}
              color={editMode ? 'primary' : 'default'}
              startIcon={editMode ? <CheckIcon /> : <EditIcon />}
              onClick={handleEditModeToggle}
            >
              {editMode ? 'Done' : 'Edit page'}
            </Button>
          </Box>
        </Box>
        <Box style={{ padding: '0 16px', marginBottom: 8 }}>
          <GithubTeamsBar
            selectedTeams={selectedTeamSlugs}
            userSelected={userSelected}
            onSelectionChange={handleTeamSelectionChange}
            onUserSelectionChange={handleUserSelectionChange}
            onTeamsLoaded={handleTeamsLoaded}
            onUsernameLoaded={handleUsernameLoaded}
            editMode={editMode}
            onSaveDefault={handleSaveDefault}
          />
        </Box>
        <Grid container spacing={3} className={classes.container}>
          <Grid item xs={12} md={6}>
            <OnboardingSection
              key={`onboarding-${refreshKey}`}
              editMode={editMode}
              onHide={() => handleSectionHidden('onboarding')}
              selectedGroupRefs={selectedGroupRefs}
              userSelected={userSelected}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TeamLinksSection
              key={`team-links-${refreshKey}`}
              editMode={editMode}
              onHide={() => handleSectionHidden('team-links')}
              selectedGroupRefs={selectedGroupRefs}
              userSelected={userSelected}
            />
          </Grid>
          <Grid item xs={12}>
            <PullRequestsSection
              key={`pull-requests-${refreshKey}`}
              editMode={editMode}
              onHide={() => handleSectionHidden('pull-requests')}
            />
          </Grid>
          <Grid item xs={12}>
            <GitRepoSection
              key={`git-repos-${refreshKey}`}
              editMode={editMode}
              onHide={() => handleSectionHidden('git-repos')}
              selectedGroupRefs={selectedGroupRefs}
              userSelected={userSelected}
            />
          </Grid>
          <Grid item xs={12}>
            <GcpProjectsSection
              key={`gcp-projects-${refreshKey}`}
              editMode={editMode}
              onHide={() => handleSectionHidden('gcp-projects')}
              selectedGroupRefs={selectedGroupRefs}
              userSelected={userSelected}
            />
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
